import type {
  Query,
  QueryCacheNotifyEvent,
  QueryClient,
  QueryCache,
} from '@tanstack/react-query';
import { estimateMemorySize } from '../../utils/memory';

// デバイスのメモリ容量(Chromium系ブラウザでのみ利用可能、0.25、0.5、1、2、4、8GBのいずれかの値を取る、未対応ブラウザでは安全側に傾け2GBとする)
const DEVICE_MEMORY_SIZE =
  'deviceMemory' in window.navigator &&
  typeof window.navigator.deviceMemory === 'number'
    ? window.navigator.deviceMemory * 1024 ** 3
    : 2 * 1024 ** 3;

// キャッシュ全体のメモリ使用量の上限(1GBを上限に、メモリ容量の1/4までとする)
const MAX_CACHE_SIZE = Math.min(DEVICE_MEMORY_SIZE / 4, 1 * 1024 ** 3);

// クエリ1件あたりの推定オーバーヘッドバイト数(メタ情報など)
const QUERY_OVERHEAD_SIZE = 1024;

// クエリが使われなくなった時間(未登録のものは使用中と判断する、WeakMapであるためクエリが削除されると自動的に消える)
const lastUsedTimes = new WeakMap<Query, number>();

// クエリデータの推定サイズ(データの参照が変わらないキャッシュとして利用できる、WeakMapであるためデータが参照されなくなると自動的に消える)
const estimatedDataSizes = new WeakMap<object, number>();

const isInUse = (query: Query): boolean => {
  // オブザーバーが登録されている、または一度もオブザーバーが外れていない(生成直後の)クエリを使用中とする
  // `query.isActive()` では `skipToken` で無効化されたクエリなどを正しく判定できないため、オブザーバーの有無で使用中かどうかを判断する
  // `prefetchQuery()` や `setQueryData()` などで生成されたクエリは `observerRemoved` イベントが発火しないため、この方法では判定ができない点に注意する
  return query.getObserversCount() > 0 || !lastUsedTimes.has(query);
};

const getLastUsedTime = (query: Query): number => {
  const lastUsedTime = lastUsedTimes.get(query);

  if (lastUsedTime === undefined) {
    // 使われなくなった時間の記録がない場合は、使用中とみなして最も新しいものとして扱う
    // 事前にフィルタリングされるためこの分岐に到達することは現状ないが、意味的に表明しておく
    return Infinity;
  }
  if (query.state.data === undefined) {
    // データを保持していないクエリ(skipTokenで無効化されたものなど)は、他のクエリのキャッシュを押し出さないよう最も古いものとして扱う
    return 0;
  }
  return lastUsedTime;
};

const estimateDataSize = (data: unknown): number => {
  const isObject = typeof data === 'object' && data !== null;

  // オブジェクトの場合はキャッシュがあるかを確認する
  if (isObject && estimatedDataSizes.has(data)) {
    return estimatedDataSizes.get(data) ?? 0;
  }

  const size = estimateMemorySize(data);

  // オブジェクトの場合はキャッシュに保存する
  if (isObject) {
    estimatedDataSizes.set(data, size);
  }

  return size;
};

const estimateQuerySize = (query: Query): number => {
  // gcTimeが0の場合はすぐに削除される一時的なクエリのため、サイズの計算から除外する
  if (query.gcTime === 0) {
    return 0;
  }
  // データサイズを計算し、Queryオブジェクト自体の概算値を加える
  return QUERY_OVERHEAD_SIZE + estimateDataSize(query.state.data);
};

const removeOldQueries = (queryCache: QueryCache): void => {
  const queries = queryCache.getAll();

  // 使用中のクエリの合計サイズを計算する
  const inUseSize = queries
    .filter((query: Query): boolean => {
      return isInUse(query);
    })
    .reduce((size: number, query: Query): number => {
      return size + estimateQuerySize(query);
    }, 0);

  const unusedQueries = queries
    .filter((query: Query): boolean => {
      return !isInUse(query);
    })
    .toSorted((query1: Query, query2: Query): number => {
      // 最後に使用された時間の新しい順に並べる
      return getLastUsedTime(query2) - getLastUsedTime(query1);
    });

  let currentSize = inUseSize;
  for (const [index, query] of unusedQueries.entries()) {
    currentSize += estimateQuerySize(query);

    if (MAX_CACHE_SIZE < currentSize) {
      // キャッシュのサイズが上限を超えた時点から、これ以降の使われなくなったクエリを全て削除する
      for (const oldQuery of unusedQueries.slice(index)) {
        queryCache.remove(oldQuery);
      }
      break;
    }
  }
};

const subscribeCacheControl = (queryClient: QueryClient): (() => void) => {
  return queryClient
    .getQueryCache()
    .subscribe((event: QueryCacheNotifyEvent): void => {
      if (event.type === 'observerRemoved') {
        // オブザーバーが外れた時間を上書きで更新することで、全てのオブザーバーが外れた時間(=最後に使用された時間)を記録する
        lastUsedTimes.set(event.query, Date.now());

        // 既にキャッシュサイズの上限を超えている可能性があるため、使われなくなったクエリが生まれたこのタイミングで古いキャッシュを削除する
        // キャッシュキーの切替時に `removeObserver()` と `addObserver()` が連続して呼ばれる場合があるため、マイクロタスクで遅延させてから削除処理を行う
        queueMicrotask((): void => {
          removeOldQueries(queryClient.getQueryCache());
        });
      } else if (event.type === 'updated' && event.action.type === 'success') {
        // 新たにキャッシュが追加されたこのタイミングで、キャッシュサイズを確認して古いキャッシュを削除する
        // クエリの更新イベントが送られた後も内部処理が同期的に行われているため、クエリ更新が完了するのを待つために、マイクロタスクで遅延させてから削除処理を行う
        queueMicrotask((): void => {
          removeOldQueries(queryClient.getQueryCache());
        });
      }
    });
};

export { subscribeCacheControl };
